import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import RouteView from '../views/RouteView.vue'
import NewRouteView from '../views/NewRouteView.vue'
import ProfileView from '../views/ProfileView.vue'
import ProfileEditView from '../views/ProfileEditView.vue'
import ProfileBookingsView from '../views/ProfileBookingsView.vue'
import LocationView from '../views/LocationView.vue'
import { useAccountStore } from '@/store/account'
import { hide } from '@popperjs/core'
import { App } from '@capacitor/app'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // {
    //   path: '/403',
    //   name: '403',
    //   component: () => import('../views/errors/ErrorPage403.vue'),
    // },
    // {
    //   path: '/404',
    //   name: '404',
    //   component: () => import('../views/errors/ErrorPage404.vue'),
    // },
    // {
    //   path: '/500',
    //   name: '500',
    //   component: () => import('../views/errors/ErrorPage500.vue'),
    // },
    {
      path: '/',
      name: 'home',
      component: HomeView
    },
    {
      path: '/route',
      name: 'route',
      component: RouteView
    },
    {
      path: '/route/new',
      name: 'new-route',
      component: NewRouteView
    },
    {
      path: '/location/:id?',
      name: 'location',
      component: LocationView
    },
    {
      path: '/location/:id/edit',
      name: 'edit-location',
      component: LocationView
    },
    {
      path: '/profile',
      name: 'profile',
      component: ProfileView
    },
    {
      path: '/profile/edit',
      name: 'edit-profile',
      component: ProfileEditView
    },
    {
      path: '/profile/bookings',
      name: 'bookings',
      component: ProfileBookingsView
    },
    {
      path: '/products',
      name: 'products',
      component: () => import('@/views/ProductsView.vue')
    },
    {
      path: '/purchase/:id?',
      name: 'purchase',
      component: () => import('@/views/PurchaseView.vue')
    },
    {
      path: '/confirm-purchase/:id?', // The checkout session ID
      name: 'purchase-success',
      component: () => import('@/views/PurchaseSuccessView.vue')
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/Login/RegisterView.vue'),
      meta: {
        public: true
      }
    },
    {
      path: '/register-siteowner',
      name: 'register-siteowner',
      component: () => import('@/views/Login/SiteownerRegisterView.vue'),
      meta: {
        public: true
      }
    },
    {
      path: '/register/verify',
      name: 'register-verify',
      component: () => import('@/views/Login/2FaView.vue'),
      meta: {
        public: true
      },
      beforeEnter: (to, from, next) => {
        const accountStore = useAccountStore()
        const { register_form } = accountStore
        if (!register_form?.email || !register_form?.phone) {
          return next({
            name:
              accountStore.register_flow === 'siteowner'
                ? 'register-siteowner'
                : 'register'
          })
        }
        next()
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login/LoginView.vue'),
      meta: {
        public: true
      }
    },
    {
      path: '/login/phone',
      name: 'login-phone',
      component: () => import('@/views/Login/PhoneSetupView.vue'),
      meta: {
        public: true
      },
      beforeEnter: (to, from, next) => {
        const accountStore = useAccountStore()
        const { login_form } = accountStore
        if (!login_form.email || !login_form.password) {
          return next({ name: 'login' })
        }
        next()
      }
    },
    {
      path: '/login/2fa',
      name: '2fa',
      component: () => import('@/views/Login/2FaView.vue'),
      meta: {
        public: true
      },
      beforeEnter: (to, from, next) => {
        const accountStore = useAccountStore()
        const { login_form } = accountStore
        if (!login_form.email || !login_form.password) {
          return next({ name: 'login' })
        }
        next()
      }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/Login/ForgotPasswordView.vue'),
      meta: {
        public: true
      }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/Login/ResetPasswordView.vue'),
      meta: {
        public: true
      }
    },
    {
      path: '/company-test',
      name: 'company-test',
      component: () => import('@/views/CompanyTest.vue'),
      meta: {
        public: true
      }
    },
    {
      path: '/update-required',
      name: 'update-required',
      component: () => import('@/views/UpdateRequiredView.vue'),
      meta: {
        public: true,
        isFullscreen: true
      }
    },
    {
      path: '/app-link/:platform/:link(.*)',
      name: 'app-link',
      component: {
        template: '<div>Redirecting...</div>' // A simple template to show while redirecting
      },
      beforeEnter: (to, from, next) => {
        // Extract platform from the URL
        const platform = to.params.platform as string // Extract the platform from the route parameters
        // const link = new URL(to.params.link as string) // Extract the link from the route parameters
        const link = to.params.link as string // Extract the link from the route parameters
        let platform_url = '' // Initialize the platform URL variable
        switch (platform) {
          case 'ios':
            platform_url = 'se.trucktrust.app://'
            break
          case 'android':
            platform_url = 'se.trucktrust.app://'
            break
          default:
            console.error('Invalid platform:', platform) // Log an error for invalid platform
            return next(false) // Prevent navigation for invalid platform
        }

        const appUrl = new URL(`${platform_url}${link.replace(/^\/+/, '')}`) // Create a new URL object with the app URL
        appUrl.search = new URLSearchParams(
          to.query as Record<string, string>
        ).toString() // Set the search parameters to the app URL
        console.log('App link:', appUrl.toString()) // Log the app URL for debugging
        window.location.href = appUrl.toString() // Redirect to the app URL
        next(true)
      },
      meta: {
        public: true,
        isFullscreen: true
      }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  const accountStore = useAccountStore()
  const { verify } = accountStore

  // Does the page require authentication?
  if (!to.meta.public && (await verify()) === false) {
    return next({ name: 'login' })
  }

  // Render the page
  next()
})

export default router
